import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PriceCard from "./PriceCard.js";
import { SkeletonCard, StaticAlert, ExclamationIcon } from './SkepticParts.js';
import { check_server_online, fetch_api, get_member_data } from "../api/api_calls.js";



function StaticPriceCards({api_server_url, ALLOWED_PLANS}){
    
    const [tiersData, setTiersData] = useState([]);
    const [userData, setUserData] = useState({
        member_status: '', //true->paid, false->free, ''->anonymous user
        client_email: '', //{{@member.email}}
        client_stripe_id: '', //{{@member.subscriptions.[0].customer.id}}
        client_stripe_sub_id: '', //{{@member.subscriptions.[0].id}}
        current_tier: '',//{{@member.subscriptions.[0].tier.name}}
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rerenderKey, setRerenderKey] = useState(0);


    console.log('[+] App body run loading -> ', loading);

    let calc_tier_names = [];
    let calc_user_level = NaN;

    const get_subs_names = () => {   
           const tier_names = tiersData.map(t => t.name);
           return ['anon', ...tier_names]; //Anon, Free are 0,1 indexes, additional are paid ones generated by Ghost CMS        
    }
    const get_user_level = (tier_levels_name) => {
        if(userData.member_status === ''){
            return 0; //anon user
        }
        if(userData.member_status === false){
            return 1; // Free member
        }
        if(userData.member_status === true){
            //TODO - handle situation when user does not match current tiers. e.g. it returns -1
            let tier_number = tier_levels_name.indexOf(userData.current_tier);
            if(tier_number === -1) throw new Error('Uknown Tier name, tier returned -1');
            return tier_number;
        }
    }

    //Calc Subs level including Anon user on index 0!
    //Calc current user level based on these tier names
    calc_tier_names = get_subs_names();
    calc_user_level = get_user_level(calc_tier_names);


    useEffect(() => {
        console.log('[+] Component App mounted');
        const rerender_from_child = () =>{
            console.log('[+] Parent rerender');
            setLoading(true);
            setTimeout(()=>{
                let get_rerenderKey = rerenderKey;
                setRerenderKey(get_rerenderKey+1);
            },500);

        }
        const init = async() => {
            try {                
                const check_server = await check_server_online(api_server_url);//Fail if server is not accesible                
                const get_tiers = await fetch_api('tiers', '&include=benefits,monthly_price,yearly_price&filter=active:true');                

                if (get_tiers.error) {
                    throw new Error(get_tiers.error);
                }

                //Filter Tiers according allowed array!!!

                let filter_tiers = [];
                for(let item of get_tiers.tiers){
                    for(let i of ALLOWED_PLANS){
                        if(i.name.toLowerCase() === item.name.toLowerCase()){
                            item.price = i.interval == 'month' ? item.monthly_price : item.yearly_price;
                            item.interval = i.interval;
                            delete item.yearly_price;
                            delete item.monthly_price;
                            filter_tiers.push(item);
                            break;
                        }
                    }                    
                }

                setTiersData(filter_tiers);

                const mem_data = await get_member_data();

                //console.log('[+] Member data -> ', mem_data);
                let activeSubsription = mem_data?.subscriptions?.length ? mem_data.subscriptions.filter(item=>item.status === 'active') : [];
                console.log('[+] Active Subsriptions -> ', activeSubsription);
                setUserData({
                    member_status: mem_data?.paid ?? '', // true -> paid, false -> free, '' -> anonymous user
                    client_email: mem_data?.email ?? '', // {{@member.email}}                  

                    client_stripe_id: activeSubsription.length
                      ? activeSubsription[0]?.customer?.id ?? ''
                      : '',
                    //We assume the first subscription in subs array is active one
                    client_stripe_sub_id: activeSubsription.length
                      ? activeSubsription[0]?.id ?? ''
                      : '',
                  
                    current_tier: activeSubsription.length
                      ? activeSubsription[0]?.tier?.name ?? ''
                      : '',

                    // Detect if the user is complimentary
                    comped: activeSubsription.length
                        ? (
                            !activeSubsription[0]?.id &&  // Subscription ID is empty
                            activeSubsription[0]?.price?.amount === 0 && // Complimentary price
                            activeSubsription[0]?.price?.nickname === "Complimentary" // Marked as complimentary
                        )
                        : false
                  });


                
            } catch (error) {
                setError(error.message); // Set the error state
            } finally {
                setLoading(false); // Set loading to false regardless of success or error
            }
        }
        init();

        window.addEventListener('rerenderStaticPricingCards', rerender_from_child); //We use this when user upgrade account, to rerender pricing cards

        return () => {
          window.removeEventListener('rerender-other-app', rerender_from_child);
        };
        

    }, [rerenderKey]); // Empty dependency array means this runs once on mount



    const fadeInVariant = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
    };

    const RenderSkeleton = () => {
        return(
            <div className='flex flex-wrap mt-5 justify-center w-full py-[--gap-67-5] gap-y-3'>
            {Array(4).fill(null).map((_, index) => (
                <div key={index} className='w-full sm:w-1/2 md1:max-w-[350px] sm:px-1.5'>
                <SkeletonCard className={'max-h-[538px]'}/> 
                </div>    
            ))}                
            </div>
        )
    }

    return (
        <div className="">
            {loading && (
                <RenderSkeleton/>
            )}

            {error && <div className='flex flex-col items-center justify-center py-16'>
                <StaticAlert className={'-mb-10'} title={'Error'} message={error}/>
                <RenderSkeleton/>
            </div>}

            {!loading && !error && ( 
                <>
                    <motion.div 
                    initial="hidden" 
                    animate="visible" 
                    className="flex flex-wrap py-[--gap-67-5] justify-center gap-y-3"
                    variants={ { visible: { transition: { staggerChildren: 0.1 }} } }                    
                    >
                      {tiersData.map(tierDetails => (
                        <motion.div
                            className='flex w-full sm:w-1/2 md1:max-w-[360px] sm:px-1.5'
                            key={tierDetails.id}
                            variants={fadeInVariant}
                            transition={{ duration: 0.2 }} // Customize the fade-in duration
                        >
                        <PriceCard key={tierDetails.id} tier={tierDetails} subs_levels={calc_tier_names} user_level={calc_user_level} user_data={userData} />
                        </motion.div>
                      ))}
                    </motion.div>
                    
                    <div className='flex w-full items-center justify-center relative -mt-5'>
                      <div className='w-full flex flex-col lg:flex-row max-w-[1428px]'>

                          <div className='w-full lg:w-[45%] xl:w-auto py-2.5 flex items-center justify-end'>
                            <span className='inline-block mr-4 text-4xl mt-1'>🎁</span>
                              <small className='block text-[0.8125rem] mt-px leading-[1.33] uppercase text-skeptic-charcoal dark:text-skeptic-foreground-dark'> 
                                Looking for the perfect gift? Inspire curiosity and critical thinking with a <a className='text-skeptic-primary underline italic' href=" https://shop.skeptic.com/products/gift-subscription" alt="link to shopify to buy gift subscription">gift subscription to Skeptic.</a> 
                              </small>
                          </div>
                          
                          <div className="hidden lg:block w-[10%] xl:w-[12%] relative">
                            <span className='block absolute left-1/2 -translate-x-1/2 top-0 w-px h-full bg-skeptic-charcoal dark:bg-skeptic-foreground-dark'></span>
                          </div>

                          <div className='w-full lg:w-[45%] xl:w-auto flex self-center items-center ml-1 px-6 py-2.5 mt-3 lg:mt-0 border border-skeptic-gray dark:border-skeptic-gray-dark rounded-md xl:min-w-[500px] 1xl:min-w-[560px] 2xl:min-w-[590px]'>
                              <div className='mr-3'>
                                <ExclamationIcon className={'size-[1.375rem]'}/>
                              </div>
                              <small className='block text-[0.8125rem] mt-px leading-[1.33] uppercase text-skeptic-charcoal dark:text-skeptic-foreground-dark'>
                                  Subscribed on OUR old website or paid by check? <a className='text-skeptic-primary underline italic' href="/request-account" alt="link to form for requesting account on skeptic magazine">Request an account.</a>
                              </small>
                          </div>

                      </div>
                    </div>

                    {process.env.NODE_ENV === 'development' && (
                        <div className='flex flex-col mt-5 text-skeptic-charcoal dark:text-skeptic-foreground-dark'>
                            <small className="text-xs block font-bold">Debug Info:</small>
                            <small className="text-xs block">User Data: {JSON.stringify(userData)}</small>
                            <small className="text-xs block">User Level: {calc_user_level} </small>
                            <small className="text-xs block">Subs Level: {calc_tier_names.join(', ')} </small>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default StaticPriceCards;